import {
	Component,
	ojs,
	app,
	v,
	each,
	payload,
	State,
} from "modular-openscriptjs";
import {
	either,
	oneOf,
	formatThousand,
	monetary,
	redirect,
} from "../../helpers";
import { dom, tool } from "../../fotastart";
import ProfilePic from "../ProfilePic";
import MoreOptions from "../Blocks/MoreOptions";
import ProductImageView from "./ProductImageView";
import SolidIcon from "../Blocks/SolidIcon";
import ProductCommentForm from "../Forms/ProductCommentForm";

const h = app("h");
const broker = app("broker");
const $e = app("$e");
const uc = app("uc");
const dc = app("dc");
const gc = app("gc");
const route = app("router");

// Shared with both the desktop and mobile price blocks below: how much
// (as a whole-number percent) a product's active discount knocks off its
// normal price. Returns 0 when there's no live discount to show.
function productDiscountPercent(product) {
	if (
		!product?.discounted_price ||
		!(product.discounted_price < product.price) ||
		!(product.price > 0)
	) {
		return 0;
	}
	return Math.round((1 - product.discounted_price / product.price) * 100);
}

// Shown instead of the Add to Cart / Order via WhatsApp controls when the
// product's business is currently closed - see Product::is_available on
// the backend (App\Services\AvailabilityService) for how that's computed.
// product.is_available defaults truthy (undefined !== false) for any
// response that hasn't been updated to include the field yet, so this
// only ever hides the cart controls once the API explicitly says closed.
function renderShopClosedNotice(extraClass = "w-100 py-3") {
	return h.div(
		{
			class: `${extraClass} rounded-pill fw-bold d-flex align-items-center justify-content-center gap-2 bg-light text-muted border`,
		},
		h.i({ class: "fas fa-store-slash" }),
		"Shop is currently closed"
	);
}

ojs(ProfilePic, MoreOptions, ProductImageView, SolidIcon, ProductCommentForm);

export default class MainProductView extends Component {
	cartQtyTimeouts = {};

	render(productState, ...args) {
		let product = productState;

		if (productState instanceof State) {
			product = productState.value;
		}

		if (!product?.id) return h.empty();

		let user = product?.owner;

		if (product?.is_copied) {
			user = product?.copier;
		}

		const hasImages = product.images && product.images.length > 0;

		return h.div(
			{
				class: "mb-4 p-0 p-md-4 p-lg-5",
				data_product_id: product?.id,
				$data_product_id: product?.id,
			},

			// Desktop Layout (lg+)
			h.div(
				{ class: "d-none d-lg-flex row g-5 bg-white" },

				// Left Column: Images
				h.div(
					{ class: "col-lg-7" },
					h.div(
						{
							class: "position-relative rounded-4 mb-3",
							style: "background-color: #f8f9fa;",
						},
						h.ProductImageView(product.images, {
							class: "w-100 object-fit-contain",
							style: "max-height: 80vh; min-height: 500px;",
						}),
						either(!product?.carata_handles_delivery, () =>
							h.span(
								{
									class: "position-absolute top-0 start-0 m-3 badge bg-white text-dark border shadow-sm fw-bold rounded-pill px-3 py-2",
								},
								"Seller Delivery"
							)
						)
					)
				),

				// Right Column: Info & Actions
				h.div(
					{ class: "col-lg-5" },
					h.div(
						{
							class: "sticky-top",
							style: "top: 2rem; z-index: 10;",
						},

						// Products Header
						h.div(
							{ class: "mb-4" },
							h.div(
								{
									class: "d-flex justify-content-between align-items-start mb-2",
								},
								h.h1(
									{
										class: "fw-bold text-dark fs-2 mb-0 lh-sm text-break pe-3",
									},
									product?.name
								),
								// Options Dropdown
								h.div(
									{ class: "dropdown ms-2" },
									h.MoreOptions([
										null,
										h.ul(
											{ class: "list-unstyled m-0" },
											either(
												(product.owner_id ==
													gc.user?.id &&
													product.pivot?.user_id ==
														gc.user?.id) ||
													(gc.user?.id ==
														product.pivot
															?.user_id &&
														gc.user?.id ==
															user?.id &&
														product.parent_product !=
															null),
												() =>
													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item btn-icon-text gap-1",
																onclick:
																	this.method(
																		"editProduct",
																		product
																	),
																title: "Edit Product",
															},
															h.span(
																{
																	class: "text",
																},
																"Edit"
															),
															h.span(
																{
																	class: "icon",
																},
																h.i({
																	class: "fa-solid fa-pen",
																})
															)
														)
													)
											),

											either(
												product.owner_id == gc.user?.id,
												() =>
													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item btn-icon-text gap-1",
																onclick:
																	this.method(
																		"copyProduct",
																		product
																	),
																title: "Copy Product",
															},
															h.span(
																{
																	class: "text",
																},
																"Copy & Edit"
															),
															h.span(
																{
																	class: "icon",
																},
																h.i({
																	class: "fa-solid fa-copy",
																})
															)
														)
													)
											),
											either(
												product.owner_id !=
													gc.user?.id &&
													product.is_copyable == 1,
												() =>
													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item btn-icon-text gap-1",
																onclick:
																	this.method(
																		"copyProduct",
																		product
																	),
																title: "Clone Product",
															},
															h.span(
																{
																	class: "text",
																},
																"Copy & Edit"
															),
															h.span(
																{
																	class: "icon",
																},
																h.i({
																	class: "fa-solid fa-copy",
																})
															)
														)
													)
											),
											either(
												product.is_private == 0 ||
													(gc.user.id == user?.id &&
														product.owner_id ==
															gc.user.id &&
														product.pivot
															?.user_id ==
															user?.id),
												() => [
													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item btn-icon-text gap-1",
																title: "Share on WhatsApp",
																onclick:
																	this.method(
																		"shareProduct",
																		product
																	),
															},
															h.span(
																{
																	class: "text text-success",
																},
																"Share"
															),
															h.span(
																{
																	class: "icon text-success",
																},
																h.i({
																	class: "fa-brands fa-whatsapp",
																})
															)
														)
													),

													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item btn-icon-text gap-1",
																title: "Share on WhatsApp",
																onclick:
																	this.method(
																		"showQrCode",
																		product
																	),
															},
															h.span(
																{
																	class: "text",
																},
																"Share (QR)"
															),
															h.span(
																{
																	class: "icon",
																},
																h.i({
																	class: "fas fa-qrcode",
																})
															)
														)
													),
												]
											),

											either(
												(!route.is("user.view-cart") &&
													product.owner_id ==
														gc.user?.id &&
													product.pivot?.user_id ==
														gc.user?.id) ||
													(gc.user?.id ==
														product.pivot
															?.user_id &&
														gc.user?.id ==
															user?.id &&
														product.parent_product !=
															null),
												() =>
													h.li(
														h.div(
															{
																role: "button",
																class: "dropdown-item dropdown-item-danger btn-icon-text gap-1",
																title: "Delete Product",
																onclick:
																	this.method(
																		"deleteProduct",
																		product
																	),
															},
															h.span(
																{
																	class: "text",
																},
																"Delete"
															),
															h.span(
																{
																	class: "icon",
																},
																h.i({
																	class: "fa-solid fa-trash-can",
																})
															)
														)
													)
											)
										),
									])
								)
							),
							// Seller Info Compact
							h.div(
								{
									class: "d-flex align-items-center gap-2 cursor-pointer mb-3",
									role: "button",
									onclick: h.func(
										"route.to",
										"user.view-profile",
										{
											user:
												user?.id == gc.user?.id
													? "current"
													: user?.id,
										}
									),
								},
								h.ProfilePic(user, {
									class: "rounded-circle border",
									style: "width: 24px; height: 24px;",
								}),
								h.span(
									{ class: "text-secondary small fw-medium" },
									"Added by " + user?.display_name
								)
							)
						),

						// Price Section
						h.div(
							{ class: "mb-4" },
							either(
								product?.discounted_price &&
									product?.discounted_price < product?.price,
								() => [
									h.div(
										{
											class: "d-flex align-items-center gap-2",
										},
										h.span(
											{ class: "text-dark fw-bold fs-2" },
											monetary(
												product?.discounted_price,
												userCurrency
											)
										),
										h.span(
											{
												class: "badge bg-danger rounded-pill px-2 d-inline-flex align-items-center gap-1",
											},
											h.i({ class: "bi bi-tag-fill" }),
											`-${productDiscountPercent(product)}%`
										)
									),
									h.span(
										{
											class: "text-secondary text-decoration-line-through",
										},
										monetary(product?.price, userCurrency)
									),
									either(product?.deals?.[0], () =>
										h.div(
											{
												class: "text-danger small fw-semibold mt-1 d-flex align-items-center gap-1",
											},
											h.i({ class: "bi bi-stars" }),
											`Deal: ${product?.deals?.[0]?.name}` +
												(productDiscountPercent(product)
													? ` — ${productDiscountPercent(product)}% off`
													: ""),
										)
									),
								],
								() =>
									h.span(
										{ class: "text-dark fw-bold fs-2" },
										monetary(product?.price, userCurrency)
									)
							)
						),

						// Description
						either(product?.plain_description, () =>
							h.div(
								{ class: "mb-5" },
								h.p(
									{
										class: "text-secondary",
										style: "white-space: pre-line; line-height: 1.6;",
									},
									product?.plain_description
								)
							)
						),

						// Action Buttons
						h.div(
							{ class: "d-grid gap-3 mb-5" },
							either(
								product?.is_available === false,
								() => renderShopClosedNotice(),
								() =>
									either(
								product.chat_to_order,
								() =>
									h.button(
										{
											class: "btn btn-success w-100 rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm",
											onclick: this.method(
												"initiateChatOrder",
												[product.id]
											),
										},
										h.i({
											class: "fa-brands fa-whatsapp fs-5",
										}),
										"Order via WhatsApp"
									),
								() =>
									v(uc.cartMap, (cartMap) => {
										const inCart =
											product?.id in cartMap.value;
										return either(
											inCart,
											// Quantity Controls
											() =>
												h.div(
													{
														class: "d-flex gap-3",
													},
													h.div(
														{
															class: "d-flex align-items-center border rounded-pill p-1 px-2 bg-light",
														},
														h.button(
															{
																class: "btn btn-sm btn-link text-dark p-2 text-decoration-none",
																onclick:
																	this.method(
																		"decrementQuantity",
																		[
																			`mpv-pq-d-${product?.id}`,
																			product?.id,
																		]
																	),
															},
															h.i({
																class: "fas fa-minus",
															})
														),
														h.span(
															{
																class: "fw-bold fs-5 px-3 font-monospace",
																id: `mpv-pq-d-${product?.id}`,
															},
															cartMap.value[
																product?.id
															]?.item?.qty
														),
														h.button(
															{
																class: "btn btn-sm btn-link text-dark p-2 text-decoration-none",
																onclick:
																	this.method(
																		"incrementQuantity",
																		[
																			`mpv-pq-d-${product?.id}`,
																			product?.id,
																		]
																	),
															},
															h.i({
																class: "fas fa-plus",
															})
														)
													),
													h.button(
														{
															class: "btn btn-dark flex-grow-1 rounded-pill fw-bold",
															onclick: h.func(
																"route.to",
																"user.view-cart"
															),
														},
														"View Cart"
													)
												),
											// Add to Cart
											() =>
												h.button(
													{
														class: "btn btn-dark w-100 rounded-pill py-3 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm",
														onclick: this.method(
															"addToCart",
															product
														),
													},
													h.i({
														class: "fas fa-cart-plus",
													}),
													"Add to Cart"
												)
										);
									})
							)
							)
						),

						// Social Actions (Like/Comment/Save)
						h.div(
							{
								class: "d-flex justify-content-between align-items-center border-top pt-4",
							},
							h.div(
								{ class: "d-flex gap-3" },
								h.button(
									{
										class: "btn btn-link text-decoration-none p-0 d-flex align-items-center gap-2",
										onclick: product?.current_like?.length
											? this.method("unlike", product)
											: this.method("like", product),
									},
									h.i({
										class:
											(product?.current_like?.length
												? "fas text-danger"
												: "far text-secondary") +
											" fa-heart fa-lg",
									}),
									h.span(
										{ class: "text-secondary fw-medium" },
										formatThousand(product?.like_count)
									)
								),
								h.button(
									{
										class: "btn btn-link text-decoration-none p-0 d-flex align-items-center gap-2 ms-3",
										onclick: this.method(
											"comment",
											product
										),
									},
									h.i({
										class:
											(product?.current_comment
												? "fas text-primary"
												: "far text-secondary") +
											" fa-comment fa-lg",
									}),
									h.span(
										{ class: "text-secondary fw-medium" },
										formatThousand(product?.comment_count)
									)
								)
							),
							either(product?.is_saveable == 1, () =>
								h.button(
									{
										class: "btn btn-light rounded-circle",
										style: "width: 40px; height: 40px;",
										onclick: product?.current_bookmark
											? this.method(
													"removeBookmark",
													product
											  )
											: this.method("bookmark", product),
									},
									h.i({
										class:
											(product?.current_bookmark
												? "fas text-warning"
												: "far text-secondary") +
											" fa-bookmark",
									})
								)
							)
						)
					)
				)
			),

			// Mobile Layout (d-lg-none)
			h.div(
				{
					class: "d-lg-none card border-0 shadow-sm rounded-4 mb-3 bg-white",
				},

				// Header Section
				h.div(
					{
						class: "card-header bg-white border-0 p-3 d-flex align-items-center justify-content-between",
					},
					h.div(
						{
							class: "d-flex align-items-center gap-2 cursor-pointer",
							onclick: h.func("route.to", "user.view-profile", {
								user:
									user?.id == gc.user?.id
										? "current"
										: user?.id,
							}),
						},
						// Avatar
						h.div(
							{ class: "position-relative" },
							h.ProfilePic(user, {
								class: "rounded-circle object-fit-cover border border-light-subtle",
								style: "width: 40px; height: 40px;",
							}),
							either(user?.is_verified, () =>
								h.i({
									class: "fas fa-check-circle text-primary position-absolute bottom-0 end-0 bg-white rounded-circle border border-white",
									style: "font-size: 12px;",
								})
							)
						),
						// User Info
						h.div(
							{ class: "d-flex flex-column lh-1" },
							h.span(
								{ class: "fw-bold text-dark small" },
								user?.display_name
							),
							h.span(
								{ class: "text-secondary x-small" },
								"@" + user?.username
							)
						)
					),

					// Dropdown Options
					h.div(
						{ class: "dropdown" },
						h.MoreOptions([
							null,
							h.ul(
								{
									class: "dropdown-menu shadow-lg border-0 rounded-3 p-2",
								},
								// Edit
								either(
									(product.owner_id == gc.user?.id &&
										product.pivot?.user_id ==
											gc.user?.id) ||
										(gc.user?.id ==
											product.pivot?.user_id &&
											gc.user?.id == user?.id &&
											product.parent_product != null),
									() =>
										h.li(
											h.button(
												{
													class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2",
													onclick: this.method(
														"editProduct",
														product
													),
												},
												h.i({
													class: "fas fa-pen fa-fw text-secondary",
												}),
												"Edit"
											)
										)
								),
								// Copy
								either(product.owner_id == gc.user?.id, () =>
									h.li(
										h.button(
											{
												class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2",
												onclick: this.method(
													"copyProduct",
													product
												),
											},
											h.i({
												class: "fas fa-copy fa-fw text-secondary",
											}),
											"Make a Copy"
										)
									)
								),
								// Clone
								either(
									product.owner_id != gc.user?.id &&
										product.is_copyable == 1,
									() =>
										h.li(
											h.button(
												{
													class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2",
													onclick: this.method(
														"copyProduct",
														product
													),
												},
												h.i({
													class: "fas fa-clone fa-fw text-secondary",
												}),
												"Clone Product"
											)
										)
								),
								// Share
								either(
									product.is_private == 0 ||
										(gc.user.id == user?.id &&
											product.owner_id == gc.user.id),
									() => [
										h.li(
											h.hr({
												class: "dropdown-divider my-1",
											})
										),
										h.li(
											h.button(
												{
													class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2 text-success",
													onclick: this.method(
														"shareProduct",
														product
													),
												},
												h.i({
													class: "fab fa-whatsapp fa-fw",
												}),
												"Share on WhatsApp"
											)
										),
										h.li(
											h.button(
												{
													class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2",
													onclick: this.method(
														"showQrCode",
														product
													),
												},
												h.i({
													class: "fas fa-qrcode fa-fw text-secondary",
												}),
												"Show QR Code"
											)
										),
									]
								),
								// Delete
								either(
									(product.owner_id == gc.user?.id &&
										product.pivot?.user_id ==
											gc.user?.id) ||
										(gc.user?.id ==
											product.pivot?.user_id &&
											gc.user?.id == user?.id &&
											product.parent_product != null),
									() => [
										h.li(
											h.hr({
												class: "dropdown-divider my-1",
											})
										),
										h.li(
											h.button(
												{
													class: "dropdown-item rounded-2 py-2 d-flex align-items-center gap-2 text-danger",
													onclick: this.method(
														"deleteProduct",
														product
													),
												},
												h.i({
													class: "fas fa-trash-alt fa-fw",
												}),
												"Delete"
											)
										),
									]
								)
							),
						])
					)
				),

				// Product Image or Info Placeholder
				either(hasImages, () =>
					h.div(
						{ class: "position-relative" },
						h.ProductImageView(product.images, {
							class: "rounded-0 w-100 object-fit-cover",
							style: "min-height: 250px; max-height: 500px;",
						}),
						// Seller Delivery Badge
						either(!product?.carata_handles_delivery, () =>
							h.span(
								{
									class: "position-absolute top-0 start-0 m-3 badge bg-warning text-dark border border-white shadow-sm fw-bold rounded-pill px-3",
								},
								"Seller Delivery"
							)
						)
					)
				),

				// Product Details Body
				h.div(
					{ class: "card-body p-3" },

					// Time and Category (Optional context)
					h.div(
						{
							class: "d-flex justify-content-between align-items-center mb-2",
						},
						h.span(
							{ class: "text-muted small" },
							product?.shared_at
						),
						either(
							product?.discounted_price &&
								product?.discounted_price < product?.price,
							() =>
								h.span(
									{
										class: "badge bg-danger rounded-pill d-inline-flex align-items-center gap-1",
									},
									h.i({ class: "bi bi-tag-fill" }),
									`-${productDiscountPercent(product)}%`
								)
						)
					),

					// Title
					h.h5(
						{
							class: "card-title fw-bold text-dark mb-1 cursor-pointer",
							onclick: h.func(
								"route.to",
								"product.view-product",
								{
									productId: product?.id,
									u: user?.id,
								}
							),
						},
						product?.name
					),

					// Price (Bolder & Bigger)
					h.div(
						{ class: "d-flex align-items-baseline gap-2 mb-3" },
						either(
							product?.discounted_price &&
								product?.discounted_price < product?.price,
							() => [
								h.span(
									{ class: "text-primary fw-bold fs-4" },
									monetary(
										product?.discounted_price,
										userCurrency
									)
								),
								h.span(
									{
										class: "text-muted text-decoration-line-through small",
									},
									monetary(product?.price, userCurrency)
								),
							],
							() =>
								h.span(
									{ class: "text-primary fw-bold fs-4" },
									monetary(product?.price, userCurrency)
								)
						)
					),

					// Description Logic
					either(product?.plain_description, () =>
						h.div(
							{ class: "mb-3" },
							h.div(
								{
									class: `text-secondary small text-break ${
										hasImages ? "d-none" : ""
									}`, // Hide by default if images exist
									hook: `mpv-${product.id}-description`,
									style: hasImages
										? ""
										: "display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; overflow: hidden;",
								},
								product?.plain_description
							),
							// Toggle Button
							h.div(
								{
									class: "text-primary small fw-semibold cursor-pointer mt-1",
									role: "button",
									hook: `mpv-${product.id}-desc-text`,
									onclick: this.method("toggleDescription", [
										product.id,
										hasImages,
									]),
								},
								hasImages ? "Show description" : "Read more"
							)
						)
					),

					// Action Buttons (Add to Cart / Quantity)
					h.div(
						{ class: "mb-2" },
						either(
								product?.is_available === false,
								() => renderShopClosedNotice(),
								() =>
									either(
							product.chat_to_order,
							() =>
								h.button(
									{
										class: "btn btn-outline-success w-100 rounded-pill py-2 fw-bold d-flex align-items-center justify-content-center gap-2",
										onclick: this.method(
											"initiateChatOrder",
											[product.id]
										),
									},
									h.i({
										class: "fa-brands fa-whatsapp fs-5",
									}),
									"Order via WhatsApp"
								),
							() =>
								v(uc.cartMap, (cartMap) => {
									const inCart = product?.id in cartMap.value;
									return either(
										inCart,
										// Quantity Controls
										() =>
											h.div(
												{
													class: "d-flex gap-2 align-items-center",
												},
												h.button(
													{
														class: "btn btn-outline-primary rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0",
														style: "width: 40px; height: 40px;",
														onclick: this.method(
															"decrementQuantity",
															[
																`mpv-pq-${product?.id}`,
																product?.id,
															]
														),
													},
													h.i({
														class: "fas fa-minus",
													})
												),
												h.span(
													{
														class: "fw-bold fs-5 flex-grow-1 text-center font-monospace",
														id: `mpv-pq-${product?.id}`,
													},
													cartMap.value[product?.id]
														?.item?.qty
												),
												h.button(
													{
														class: "btn btn-outline-primary rounded-circle p-0 d-flex align-items-center justify-content-center flex-shrink-0",
														style: "width: 40px; height: 40px;",
														onclick: this.method(
															"incrementQuantity",
															[
																`mpv-pq-${product?.id}`,
																product?.id,
															]
														),
													},
													h.i({
														class: "fas fa-plus",
													})
												),
												// View Cart Link
												h.button(
													{
														class: "btn btn-primary rounded-pill px-4 ms-2 fw-medium",
														onclick: h.func(
															"route.to",
															"user.view-cart"
														),
													},
													"View Cart"
												)
											),
										// Add to Cart Button
										() =>
											h.div(
												{ class: "d-flex gap-2" },
												h.button(
													{
														class: "btn btn-primary w-100 rounded-pill py-2 fw-bold d-flex align-items-center justify-content-center gap-2 shadow-sm",
														onclick: this.method(
															"addToCart",
															product
														),
													},
													h.i({
														class: "fas fa-cart-plus",
													}),
													"Add to Cart"
												),
												either(
													product.owner_id !=
														gc.user?.id &&
														product.is_copyable ==
															1,
													() =>
														h.button(
															{
																class: "btn btn-outline-secondary rounded-circle p-0 d-flex align-items-center justify-content-center",
																style: "width: 42px; height: 42px;",
																onclick:
																	this.method(
																		"copyProduct",
																		product
																	),
																title: "Clone",
															},
															h.i({
																class: "fas fa-clone",
															})
														)
												)
											)
									);
								})
						)
							)
					)
				),

				// Actions Footer (Like, Comment, Share)
				h.div(
					{ class: "card-footer bg-white border-top p-2" },
					h.div(
						{
							class: "d-flex justify-content-around align-items-center",
						},
						// Like
						h.button(
							{
								class: "btn btn-link text-decoration-none text-dark p-2 d-flex flex-column align-items-center gap-1",
								onclick: product?.current_like?.length
									? this.method("unlike", product)
									: this.method("like", product),
							},
							h.i({
								class:
									(product?.current_like?.length
										? "fas text-danger"
										: "far text-secondary") +
									" fa-heart fs-5 animated-icon",
							}),
							h.span(
								{ class: "small text-muted" },
								formatThousand(product?.like_count)
							)
						),
						// Comment
						h.button(
							{
								class: "btn btn-link text-decoration-none text-dark p-2 d-flex flex-column align-items-center gap-1",
								onclick: this.method("comment", product),
							},
							h.i({
								class:
									(product?.current_comment
										? "fas text-primary"
										: "far text-secondary") +
									" fa-comment fs-5",
							}),
							h.span(
								{ class: "small text-muted" },
								formatThousand(product?.comment_count)
							)
						),
						// Save/Bookmark
						either(product?.is_saveable == 1, () =>
							h.button(
								{
									class: "btn btn-link text-decoration-none text-dark p-2 d-flex flex-column align-items-center gap-1",
									onclick: product?.current_bookmark
										? this.method("removeBookmark", product)
										: this.method("bookmark", product),
								},
								h.i({
									class:
										(product?.current_bookmark
											? "fas text-warning"
											: "far text-secondary") +
										" fa-bookmark fs-5",
								}),
								h.span(
									{ class: "small text-muted" },
									formatThousand(product?.save_count)
								)
							)
						)
					)
				)
			),

			...args
		);
	}

	toggleDescription(productId, hasImages) {
		let description = dom.get(`[hook="mpv-${productId}-description"]`);
		let toggleBtn = dom.get(`[hook="mpv-${productId}-desc-text"]`);

		if (hasImages) {
			// Logic when images exist: Toggle d-none
			if (description.classList.contains("d-none")) {
				description.classList.remove("d-none");
				toggleBtn.innerText = "Hide description";
			} else {
				description.classList.add("d-none");
				toggleBtn.innerText = "Show description";
			}
		} else {
			// Logic when no images: Toggle line clamp
			if (description.style.webkitLineClamp) {
				description.style.webkitLineClamp = "";
				description.style.display = "block";
				toggleBtn.innerText = "Show less";
			} else {
				description.style.webkitLineClamp = "2";
				description.style.display = "-webkit-box";
				toggleBtn.innerText = "Read more";
			}
		}
	}

	incrementQuantity(target, productId) {
		this.changeValue("+", target, productId);
	}

	decrementQuantity(target, productId) {
		this.changeValue("-", target, productId);
	}

	changeValue(type, target, productId) {
		target = dom.id(target);

		let current = Number(target.innerHTML);

		switch (type) {
			case "+": {
				target.innerHTML = Number(target.innerHTML) + 1;
				break;
			}
			case "-": {
				target.innerHTML = Math.max(Number(target.innerHTML) - 1, 1);
				break;
			}
		}

		let qty = Number(target.innerHTML);

		if (current != qty) {
			clearTimeout(this.cartQtyTimeouts[`t-${productId}`]);
			this.cartQtyTimeouts[`t-${productId}`] = setTimeout(() => {
				broker.send(
					$e.cart.needs.itemUpdate,
					payload({
						item: {
							id: productId,
						},
						quantity: qty,
					})
				);

				clearTimeout(this.cartQtyTimeouts[`t-${productId}`]);
			}, 500);
		}
	}

	addToCart(product) {
		if (product.id in uc.cartMap.value) {
			return this.updateItem(uc.cartMap.value[product.id].item);
		}

		broker.broadcast($e.cart.itemSelected, payload({ item: product }));
	}

	editProduct(product) {
		broker.send($e.product.needs.update, payload({ item: product }));
	}

	updateItem(item) {
		broker.send($e.cart.needs.itemUpdateForm, payload({ item }));
	}

	deleteProduct(product) {
		broker.send($e.product.needs.deletion, payload({ id: product.id }));
	}

	shareProduct(product) {
		broker.send(
			$e.product.needs.share,
			payload({ id: product.id, product })
		);
	}

	copyProduct(product) {
		product.original_product_id = product.id;
		delete product.id;
		broker.send($e.product.needs.creation, payload({ item: product }));
	}

	// removeItem Removed

	like(product) {
		broker.send($e.product.needs.like, payload({ product }));
	}

	unlike(product) {
		broker.send($e.product.needs.unlike, payload({ product }));
	}

	bookmark(product) {
		broker.send($e.product.needs.bookmark, payload({ product }));
	}

	comment(product) {
		if (!route.is("product.view-product")) {
			setTimeout(() => {
				broker.send($e.product.needs.comment, payload({ product }));
			}, 1000);

			redirect().to("product.view-product", { productId: product.id });
			return;
		}

		broker.send($e.product.needs.comment, payload({ product }));
	}

	bookmark(product) {
		broker.send($e.product.needs.bookmark, payload({ product }));
	}

	removeBookmark(product) {
		broker.send($e.product.needs.bookmarkRemoved, payload({ product }));
	}

	initiateChatOrder(productId) {
		broker.send(
			$e.user.needs.to.orderViaChat,
			payload({ order_product_id: productId })
		);
	}

	showQrCode(product) {
		broker.send(
			$e.qr.needs.code,
			payload({
				title: `${product.name} - Item QR Code`,
				data: `/products/${product?.id}/view`,
			})
		);
	}
}
